import type { CotaClaude, CotaMotor } from './agente';
import { ROTULO_DO_MOTOR, motorNaoTratado, type Motor } from './motor';

/**
 * Qual motor a tela SUGERE ao criar uma sessão — e nada além disso.
 *
 * **O roteamento é manual** (decisão do dono, não se reabre). Isto aqui não
 * escolhe nada: é a frase que aparece ao lado das duas cotas, e a pessoa decide.
 * Por isso a sugestão sempre vem com o MOTIVO junto — sugestão sem porquê vira
 * ordem, e ordem que a pessoa não entende ela aprende a ignorar.
 *
 * Função pura de propósito: recebe as duas cotas e a hora, não lê relógio nem
 * banco. Quem depende de `Date.now()` por dentro não tem como ser testado no
 * ponto exato em que a leitura fica velha.
 *
 * **Três motores, duas cotas (F16).** O OpenCode não tem cota — paga por
 * token —, então ele não entra na comparação: Claude e Codex continuam sendo
 * comparados como sempre, e o OpenCode só é sugerido quando os DOIS passam de
 * `LIMIAR_DE_SUGERIR_OPENCODE` (D21) e ele está disponível na máquina.
 */

/**
 * A diferença mínima para a sugestão mudar de motor.
 *
 * Sem ela, 51% contra 49% sugeriria um motor e, no minuto seguinte, o outro —
 * pela mesma folga, invertida por ruído. A pessoa veria a sugestão trocar
 * sozinha e pararia de olhar para ela, que é o pior resultado possível para um
 * conselho.
 */
export const GAP_DE_SUGESTAO = 0.15;

/**
 * Além disto, a leitura de cota conta como DESCONHECIDA.
 *
 * Número próprio, e não o mesmo do alarme de saúde (que usa uma janela bem
 * maior): as duas perguntas são diferentes. O alarme pergunta "esta máquina
 * parou de reportar?"; aqui a pergunta é "dá para decidir com este número?". A
 * janela de cinco horas do Claude se move rápido, e um retrato de três horas
 * atrás pode estar longe do que existe agora.
 *
 * Vale lembrar por que "sem leitura" é comum e não é defeito: a cota do Codex
 * chega EMPURRADA durante um turno, não por consulta. Máquina que não rodou
 * nada hoje não tem número nenhum para mostrar — e dizer isso é mais útil que
 * fingir que tem.
 */
export const IDADE_MAXIMA_DA_COTA_MIN = 120;

export interface SugestaoDeMotor {
  motor: Motor;
  /** Frase pronta, em pt-BR, para a tela mostrar. Nunca vazia. */
  motivo: string;
  /**
   * `false` quando a sugestão é só o padrão, por falta de base para comparar.
   * A tela pode usar isto para não dar destaque a um palpite.
   */
  comparou: boolean;
}

/** O motor que a casa sugere quando não há base para comparar. */
const PADRAO: Motor = 'CLAUDE';

function porcento(fracao: number): string {
  return String(Math.round(fracao * 100)) + '%';
}

function velha(em: string, agora: Date): boolean {
  const carimbo = Date.parse(em);
  // Data que não dá para ler conta como velha: preferir o "não sei" a assumir
  // que é de agora.
  if (Number.isNaN(carimbo)) return true;
  const minutos = (agora.getTime() - carimbo) / 60_000;
  return minutos > IDADE_MAXIMA_DA_COTA_MIN;
}

/**
 * A utilização que MANDA, que é a maior das janelas.
 *
 * Exportada porque a TELA mostra o mesmo número ao lado da sugestão, e uma
 * segunda conta lá significaria duas respostas para "quanto da cota já foi" —
 * com a de cima podendo dizer 98% enquanto a de baixo sugere aquele motor.
 *
 * Quem aperta é a janela mais cheia: estar em 10% na semana não ajuda se as
 * cinco horas estão em 98%. Usar a média esconderia exatamente o caso que
 * importa.
 */
export function utilizacaoDoClaude(cota: CotaClaude | null, agora: Date): number | null {
  if (cota === null || velha(cota.em, agora)) return null;
  const valores = [cota.cincoHoras?.utilizacao, cota.seteDias?.utilizacao].filter(
    (v): v is number => typeof v === 'number',
  );
  return valores.length > 0 ? Math.max(...valores) : null;
}

export function utilizacaoDoCodex(cota: CotaMotor | null, agora: Date): number | null {
  if (cota === null || velha(cota.em, agora)) return null;
  const valores = cota.janelas.map((j) => j.utilizacao);
  // Lista vazia é "cota que ainda não foi lida", não "cota zerada" — a mesma
  // distinção que o `CotaMotorSchema` guarda.
  return valores.length > 0 ? Math.max(...valores) : null;
}

/**
 * Acima disto, nos DOIS motores de cota, a sugestão passa a ser o OpenCode (D21).
 *
 * "Passam de 90%" é estrito: 90% cravado ainda é Claude ou Codex. O OpenCode
 * não gasta essas cotas — usa a chave da frota no provedor de modelos (desde o
 * ADR-0025, a assinatura do NanoGPT e depois crédito; antes, crédito por token) —, e o
 * dono decidiu que ele só aparece como conselho quando as duas assinaturas
 * estão quase no fim. Antes disso, sugerir o motor que cobra seria empurrar a
 * pessoa para gastar dinheiro com cota sobrando.
 */
export const LIMIAR_DE_SUGERIR_OPENCODE = 0.9;

/** O uso de um motor de cota, já lido. `null` é "sem leitura recente". */
interface Leitura {
  motor: Motor;
  uso: number | null;
}

interface LeituraConhecida {
  motor: Motor;
  uso: number;
}

function conhecida(l: Leitura): l is LeituraConhecida {
  return l.uso !== null;
}

/**
 * A utilização que manda para QUALQUER motor — ou `null` quando ele não tem
 * cota para medir.
 *
 * Existe para a tela parar de escrever `motor === 'CODEX' ? doCodex : doClaude`:
 * com três motores, aquele ternário mostrava a cota do Claude na linha do
 * OpenCode, calado. O OpenCode devolve `null` porque não TEM cota — paga por
 * token —, e quem desenha diz isso com as palavras dele, não com "0%".
 */
export function utilizacaoDoMotor(
  motor: Motor,
  cotas: { claude: CotaClaude | null; codex: CotaMotor | null },
  agora: Date,
): number | null {
  switch (motor) {
    case 'CLAUDE':
      return utilizacaoDoClaude(cotas.claude, agora);
    case 'CODEX':
      return utilizacaoDoCodex(cotas.codex, agora);
    case 'OPENCODE':
      return null;
    default:
      return motorNaoTratado(motor);
  }
}

export function sugerirMotor(entrada: {
  claude: CotaClaude | null;
  codex: CotaMotor | null;
  agora: Date;
  /**
   * Se o OpenCode pode ser escolhido NESTA máquina, agora (capacidade e chave).
   *
   * Obrigatório, e sem padrão: sugerir um motor que a tela mostra desabilitado
   * é pior que não sugerir — a pessoa lê o conselho, tenta, e esbarra. Quem
   * chama é quem sabe, e tem de dizer.
   */
  opencodeDisponivel: boolean;
}): SugestaoDeMotor {
  // Os motores de COTA, lidos. A ordem é a de desempate: o primeiro é o padrão.
  const leituras: Leitura[] = [
    { motor: 'CLAUDE', uso: utilizacaoDoClaude(entrada.claude, entrada.agora) },
    { motor: 'CODEX', uso: utilizacaoDoCodex(entrada.codex, entrada.agora) },
  ];
  const conhecidas = leituras.filter(conhecida);

  if (conhecidas.length === 0) {
    return {
      motor: PADRAO,
      motivo: 'Sem leitura recente de cota nos dois motores — a escolha é sua.',
      comparou: false,
    };
  }

  /*
    Com só um dos dois medido, comparar é impossível — mas calar também não
    ajuda. A frase diz o que se sabe E o que não se sabe, e a sugestão segue o
    único número que existe.

    O OpenCode NÃO entra aqui, mesmo com o conhecido acima de 90%: a D21 pede
    os DOIS acima, e o desconhecido pode estar folgado. Mandar a pessoa pagar
    por token sem saber se a outra assinatura tem sobra é o conselho caro.
  */
  const desconhecida = leituras.find((l) => !conhecida(l));
  const [unica] = conhecidas;
  if (desconhecida !== undefined && unica !== undefined) {
    const apertado = unica.uso >= 0.5 + GAP_DE_SUGESTAO / 2;
    const conhecido = ROTULO_DO_MOTOR[unica.motor];
    const outro = ROTULO_DO_MOTOR[desconhecida.motor];
    return {
      motor: apertado ? desconhecida.motor : unica.motor,
      motivo: apertado
        ? `O ${conhecido} está em ${porcento(unica.uso)} e não há leitura recente do ${outro}.`
        : `O ${conhecido} está em ${porcento(unica.uso)} e o ${outro} não tem leitura recente.`,
      comparou: false,
    };
  }

  const frase = conhecidas.map((l) => `o ${ROTULO_DO_MOTOR[l.motor]} em ${porcento(l.uso)}`).join(' e ');

  if (entrada.opencodeDisponivel && conhecidas.every((l) => l.uso > LIMIAR_DE_SUGERIR_OPENCODE)) {
    return {
      motor: 'OPENCODE',
      motivo: `As duas cotas estão no fim (${frase}): o ${ROTULO_DO_MOTOR.OPENCODE} não gasta essas cotas — usa a chave da frota no provedor de modelos.`,
      comparou: true,
    };
  }

  // Do mais folgado para o mais cheio; `sort` é estável, então o empate fica
  // na ordem de `leituras` — o Claude primeiro, que é o padrão da casa.
  const [folgado, cheio] = [...conhecidas].sort((a, b) => a.uso - b.uso);
  if (folgado === undefined || cheio === undefined) {
    // Inalcançável com os dois motores de cota lidos; o `if` existe para o tipo.
    return { motor: PADRAO, motivo: `Leitura de cota incompleta (${frase}) — a escolha é sua.`, comparou: false };
  }

  if (cheio.uso - folgado.uso < GAP_DE_SUGESTAO) {
    return {
      motor: PADRAO,
      motivo: `As duas cotas estão parecidas (${frase}) — tanto faz.`,
      comparou: true,
    };
  }

  return {
    motor: folgado.motor,
    motivo: `O ${ROTULO_DO_MOTOR[folgado.motor]} tem mais folga: ${porcento(folgado.uso)} contra ${porcento(cheio.uso)}.`,
    comparou: true,
  };
}
